import type { AssessmentDelivery, AssessmentScope } from "@peer-start/platform-contracts";
import { Button, Card } from "@peer-start/ui";
import { ArrowRight, BrainCircuit, Clock3, ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";
import { KnowledgeStatus } from "./KnowledgeStatus.js";

export function AssessmentIntro({
  delivery,
  scope = "topic",
  hasResult,
  onStart,
  onExample,
  onResult,
}: {
  delivery: AssessmentDelivery;
  scope?: AssessmentScope;
  hasResult: boolean;
  onStart: () => void;
  onExample: () => void;
  onResult: () => void;
}) {
  const { t } = useTranslation();
  const bank = delivery.definition;
  const coverage =
    scope === "route"
      ? (
          [
            "administration",
            "wounds",
            "monitoring",
            "psychosocial",
            "lifeStages",
            "safety",
            "communication",
            "professional",
          ] as const
        ).map((id) => ({ id, title: t(`knowledgeAssessment.routeCoverage.${id}`) }))
      : bank.skills;
  return (
    <div className="space-y-5">
      <Card className="border-primary/20 bg-primary/5">
        <BrainCircuit aria-hidden="true" className="mb-4 size-9 text-primary" />
        <h2 className="text-2xl font-medium">{t("knowledgeAssessment.introTitle")}</h2>
        <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">
          {t("knowledgeAssessment.intro")}
        </p>
        <div className="my-6 grid gap-4 sm:grid-cols-3">
          <div>
            <strong className="text-2xl">{bank.items.length}</strong>
            <p className="text-sm text-muted-foreground">{t("knowledgeAssessment.questions")}</p>
          </div>
          <div>
            <strong className="inline-flex items-center gap-2 text-2xl">
              <Clock3 aria-hidden="true" className="size-5" />
              {t("knowledgeAssessment.duration", { count: bank.estimatedMinutes })}
            </strong>
            <p className="text-sm text-muted-foreground">{t("knowledgeAssessment.noTimeLimit")}</p>
          </div>
          <div>
            <strong className="text-2xl">{t("knowledgeAssessment.knowsHow")}</strong>
            <p className="text-sm text-muted-foreground">{t("knowledgeAssessment.miller")}</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button controlId="assessment.start" onClick={onStart}>
            {t(hasResult ? "knowledgeAssessment.repeat" : "knowledgeAssessment.start")}
            <ArrowRight aria-hidden="true" className="size-4" />
          </Button>
          {hasResult ? (
            <Button controlId="assessment.saved" variant="secondary" onClick={onResult}>
              {t("knowledgeAssessment.savedResult")}
            </Button>
          ) : null}
          {bank.id === "katheterisatiezorg" ? (
            <Button controlId="assessment.example" variant="ghost" onClick={onExample}>
              {t("knowledgeAssessment.exampleResult")}
            </Button>
          ) : null}
        </div>
        {hasResult ? (
          <p className="mt-3 text-sm text-muted-foreground">{t("knowledgeAssessment.sameBank")}</p>
        ) : null}
      </Card>
      <div className="grid gap-5 lg:grid-cols-2">
        <Card>
          <h2 className="text-lg font-medium">{t("knowledgeAssessment.confidenceTitle")}</h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            {t("knowledgeAssessment.confidenceHelp")}
          </p>
          <div className="mt-4 space-y-3">
            {(["mastered", "vulnerable", "learning_point", "blind_spot"] as const).map((status) => (
              <div key={status} className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-sm">{t(`knowledgeAssessment.combination.${status}`)}</span>
                <KnowledgeStatus status={status} />
              </div>
            ))}
          </div>
        </Card>
        <Card>
          <h2 className="text-lg font-medium">{t("knowledgeAssessment.coverage")}</h2>
          <ol className="mt-3 space-y-2">
            {coverage.map((skill, index) => (
              <li key={skill.id} className="flex gap-3 text-sm">
                <span className="text-primary">{index + 1}.</span>
                <span>{skill.title}</span>
              </li>
            ))}
          </ol>
        </Card>
      </div>
      <p className="flex items-start gap-2 text-sm leading-relaxed text-muted-foreground">
        <ShieldCheck aria-hidden="true" className="mt-0.5 size-5 shrink-0" />
        {t("knowledgeAssessment.boundary")}
      </p>
    </div>
  );
}
